/**
 * WYSIWYG section editor (replaces the textarea + Markdown-preview split) with
 * a formatting toolbar.
 *
 * A TipTap editor with the `tiptap-markdown` extension so the rendered,
 * directly-editable view round-trips losslessly to portable `.md` on disk —
 * the section data model is unchanged, only the edit surface is now WYSIWYG.
 *
 * Note: this reintroduces TipTap, which the TDD §9.3 had dropped in favour of
 * textarea+preview. TipTap is pure-JS (no native/toolchain weight), so it's an
 * allowed escalation of the "zero net-new deps" tenet — made at the user's
 * explicit request. Ported from Anvil's MarkdownEditor (live view + toolbar).
 */
import { useEffect, useRef } from 'react';
import type { ComponentType, ReactElement } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { Tip } from './Tip';
import { buildRichExtensions } from './lib/editor-extensions';
import { TrustedHtmlFallbackBoundary } from './lib/TrustedHtmlFallbackBoundary';
import { shouldUseTrustedHtmlFallbackForSession } from './lib/trusted-types';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Undo2,
  Redo2,
  Table as TableIcon,
  BetweenHorizontalEnd,
  BetweenVerticalEnd,
  Rows3,
  Columns3,
  Trash2,
} from 'lucide-react';

export interface SectionWysiwygProps {
  value: string;
  onChange: (markdown: string) => void;
}

function ToolbarButton({
  icon: Icon,
  label,
  active,
  disabled,
  onActivate,
}: {
  icon: ComponentType<{ size?: number }>;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onActivate: () => void;
}): ReactElement {
  return (
    <Tip label={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        disabled={disabled}
        // Keep the editor selection while clicking the toolbar.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onActivate}
        className={`peer flex h-7 w-7 items-center justify-center rounded text-sm disabled:opacity-30 ${
          active
            ? 'bg-crucible-accent/20 text-crucible-fg'
            : 'text-crucible-muted hover:bg-crucible-bg-elevated'
        }`}
      >
        <Icon size={15} />
      </button>
    </Tip>
  );
}

function Divider(): ReactElement {
  return <span className="mx-0.5 h-5 w-px self-center bg-crucible-border" />;
}

function Toolbar({ editor }: { editor: Editor }): ReactElement {
  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b border-crucible-border px-3 py-1.5"
      data-testid="design-wysiwyg-toolbar"
    >
      <ToolbarButton
        icon={Bold}
        label="Bold"
        active={editor.isActive('bold')}
        onActivate={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        icon={Italic}
        label="Italic"
        active={editor.isActive('italic')}
        onActivate={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        icon={Strikethrough}
        label="Strikethrough"
        active={editor.isActive('strike')}
        onActivate={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolbarButton
        icon={Code}
        label="Inline code"
        active={editor.isActive('code')}
        onActivate={() => editor.chain().focus().toggleCode().run()}
      />
      <Divider />
      <ToolbarButton
        icon={Heading1}
        label="Heading 1"
        active={editor.isActive('heading', { level: 1 })}
        onActivate={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <ToolbarButton
        icon={Heading2}
        label="Heading 2"
        active={editor.isActive('heading', { level: 2 })}
        onActivate={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarButton
        icon={Heading3}
        label="Heading 3"
        active={editor.isActive('heading', { level: 3 })}
        onActivate={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />
      <Divider />
      <ToolbarButton
        icon={List}
        label="Bullet list"
        active={editor.isActive('bulletList')}
        onActivate={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        icon={ListOrdered}
        label="Numbered list"
        active={editor.isActive('orderedList')}
        onActivate={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <Divider />
      <ToolbarButton
        icon={Undo2}
        label="Undo"
        disabled={!editor.can().undo()}
        onActivate={() => editor.chain().focus().undo().run()}
      />
      <ToolbarButton
        icon={Redo2}
        label="Redo"
        disabled={!editor.can().redo()}
        onActivate={() => editor.chain().focus().redo().run()}
      />
      <Divider />
      <ToolbarButton
        icon={TableIcon}
        label="Insert table"
        onActivate={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
      />
      {editor.isActive('table') && (
        <>
          <ToolbarButton
            icon={BetweenHorizontalEnd}
            label="Add row"
            onActivate={() => editor.chain().focus().addRowAfter().run()}
          />
          <ToolbarButton
            icon={Rows3}
            label="Delete row"
            onActivate={() => editor.chain().focus().deleteRow().run()}
          />
          <ToolbarButton
            icon={BetweenVerticalEnd}
            label="Add column"
            onActivate={() => editor.chain().focus().addColumnAfter().run()}
          />
          <ToolbarButton
            icon={Columns3}
            label="Delete column"
            onActivate={() => editor.chain().focus().deleteColumn().run()}
          />
          <ToolbarButton
            icon={Trash2}
            label="Delete table"
            onActivate={() => editor.chain().focus().deleteTable().run()}
          />
        </>
      )}
    </div>
  );
}

export function SectionWysiwyg({ value, onChange }: SectionWysiwygProps): ReactElement {
  const fallback = <SectionMarkdownTextarea value={value} onChange={onChange} />;
  if (shouldUseTrustedHtmlFallbackForSession()) return fallback;

  return (
    <TrustedHtmlFallbackBoundary fallback={fallback}>
      <TiptapSectionWysiwyg value={value} onChange={onChange} />
    </TrustedHtmlFallbackBoundary>
  );
}

function SectionMarkdownTextarea({ value, onChange }: SectionWysiwygProps): ReactElement {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <textarea
        data-testid="design-section-editor"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        spellCheck={false}
        className="design-wysiwyg min-h-0 flex-1 resize-none overflow-y-auto bg-transparent p-4 font-sans text-sm leading-relaxed text-crucible-fg outline-none focus:outline-none"
      />
    </div>
  );
}

function TiptapSectionWysiwyg({ value, onChange }: SectionWysiwygProps): ReactElement {
  // Guards a re-entrant update when we push an external value into the editor.
  const updatingFromExternal = useRef(false);

  const editor = useEditor({
    extensions: buildRichExtensions(),
    content: value,
    editorProps: {
      attributes: {
        class: 'design-wysiwyg outline-none min-h-full focus:outline-none',
        'data-testid': 'design-section-editor',
      },
    },
    onUpdate: ({ editor }) => {
      if (updatingFromExternal.current) return;
      const md = editor.storage.markdown.getMarkdown() as string;
      if (md !== value) onChange(md);
    },
  });

  // Push external value changes (section switch, applied draft) into the editor
  // without echoing them back through onUpdate.
  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown() as string;
    if (value === current) return;
    updatingFromExternal.current = true;
    editor.commands.setContent(value, false);
    queueMicrotask(() => {
      updatingFromExternal.current = false;
    });
  }, [value, editor]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {editor && <Toolbar editor={editor} />}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
